import { getBases } from './base';
import { config } from './config';
import { unrecordedFetch } from './recorder';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Code from the service answer (`limit`, `not_found`, `validation`…). */
    public code?: string,
  ) {
    super(message);
  }
}

/** When the answer has no `{error}` (a proxy page, an HTML error): words, not a bare status. */
function fallbackText(status: number): string {
  if (status === 401) return 'Нужно войти заново.';
  if (status === 403) return 'Не хватает прав.';
  if (status === 429) return 'Слишком часто — подождите минуту и повторите.';
  if (status === 503) return 'Баг-репорты сейчас недоступны.';
  return `Ошибка ${status}`;
}

function notify(err: ApiError): void {
  try {
    config().onApiError?.({ status: err.status, message: err.message, code: err.code });
  } catch {
    // The host callback must not break the widget.
  }
}

/** Пр1: the project every call works on, picked in the tracker header; none — the person's own (no header). */
let workingProject: string | null = null;

export function setWorkingProject(name: string | null): void {
  workingProject = name;
}

export const getWorkingProject = (): string | null => workingProject;

/**
 * Request to the service through the host proxy (`<base>/bugs/api` + path). Same semantics as facts' `request`: a network
 * failure is an `ApiError` with status 0 and human words, a non-JSON answer — `bad_response`. Goes past the recorder: the
 * report's own requests are not part of the snapshot.
 */
export async function request<T>(path: string, options: RequestInit = {}, silent = false): Promise<T> {
  const fail = (err: ApiError): ApiError => {
    if (!silent) notify(err);
    return err;
  };
  let res: Response;
  try {
    res = await unrecordedFetch(`${getBases().api}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(workingProject ? { 'X-Bug-Project': workingProject } : {}),
        ...(options.headers as Record<string, string> | undefined),
      },
    });
  } catch {
    const err = new ApiError(
      0,
      typeof navigator !== 'undefined' && navigator.onLine === false
        ? 'Нет сети: браузер офлайн. Появится связь — повторите.'
        : 'Сервер не ответил — проверьте связь и повторите.',
      'offline',
    );
    throw fail(err);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw fail(new ApiError(res.status, typeof body?.error === 'string' && body.error ? body.error : fallbackText(res.status), body?.code));
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw fail(new ApiError(res.status, 'Сервер ответил не по форме — похоже, запрос ушёл мимо API.', 'bad_response'));
  }
}

/** Attachment address for `<img>`, `<audio>` and download links (same origin, the host session cookie goes along). */
export function attachmentUrl(id: number, download = false): string {
  // An <img> cannot send the project header, so the picked project goes in the address (Пр1).
  const query = [workingProject ? `project=${encodeURIComponent(workingProject)}` : '', download ? 'download=1' : ''].filter(Boolean).join('&');
  return `${getBases().api}/bug-reports/attachments/${id}${query ? `?${query}` : ''}`;
}
